import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, AlertTriangle, Copy, Zap } from 'lucide-react';
import { useStore } from '../store';
import { selectTemplate, matchMachines, processFlow, MACHINES, TEMPLATES, MATERIALS } from '../engine/rules';
import { boltGeo, nutGeo } from '../engine/geometry';
import SequenceViewer from '../components/SequenceViewer';

const STATUS_STYLE: Record<string,string> = {
  DRAFT:'badge-gray',REVIEW:'badge-blue',PLANNING:'badge-amber',APPROVED:'badge-green',COMPLETE:'badge-green'
};

export default function JobDetail() {
  const { id } = useParams<{id:string}>();
  const navigate = useNavigate();
  const job = useStore(s => s.getJob(id!));
  const updateJob = useStore(s => s.updateJob);
  const [tab, setTab] = useState<'sequence'|'overview'|'machines'|'process'|'toolcodes'>('sequence');
  const [selectedMachine, setSelectedMachine] = useState<string|null>(null);
  const [copied, setCopied] = useState(false);

  if (!job) return <div style={{padding:40,color:'var(--t-muted)'}}>Job not found.</div>;
  const part = job.part;
  if (!part) return <div style={{padding:40,color:'var(--t-muted)'}}>No part data.</div>;

  const { template, confidence, reasoning } = selectTemplate(part);
  const machineMatches = matchMachines(part, template);
  const eligible = machineMatches.filter((m:any) => m.eligible);
  const activeMachine = selectedMachine ?? job.selected_machine ?? eligible[0]?.machine_id;
  const activeMachineData = activeMachine ? MACHINES[activeMachine] : null;
  const mat = MATERIALS[part.material_id ?? 'SAE1010'];
  const tpl = TEMPLATES[template];

  // Build geometry
  const geoList = part.part_type === 'NUT' ? nutGeo(part) : boltGeo(part);
  const wire = part.wire_diameter_mm ?? part.nominal_diameter_mm * 1.05;
  const blank = part.blank_length_mm ?? part.total_length_mm * 1.02;

  // Process flow
  const flow = processFlow(part, template, activeMachineData?.name ?? '—');

  // Tool code prefix
  const d = part.nominal_diameter_mm;
  const ds = d < 10 ? `0${Math.round(d)}` : `${Math.round(d)}`;
  const prefix = activeMachine?.includes('134') ? `134LFBM${ds}` :
                 activeMachine?.includes('164') ? `164SGSM${ds}` :
                 activeMachine?.includes('24B') ? `24B02401` :
                 activeMachine?.includes('19B') ? `19B02401` :
                 activeMachine?.includes('14B') ? `14B0${ds}M${ds}` :
                 activeMachine?.includes('11B') ? `11BSNM${ds}` : `FIQ${ds}`;

  const approve = () => updateJob(job.id, { status:'APPROVED', approved_at:new Date().toISOString() });

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom:24 }}>
        <button className="btn btn-ghost btn-sm" onClick={()=>navigate('/jobs')} style={{marginBottom:10}}>
          <ArrowLeft size={12}/> All Jobs
        </button>
        <div className="flex-between" style={{flexWrap:'wrap',gap:12}}>
          <div>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:4}}>
              <h1 style={{fontSize:20,fontFamily:'var(--mono)'}}>{job.id}</h1>
              <span className={`badge ${STATUS_STYLE[job.status]??'badge-gray'}`}>{job.status}</span>
            </div>
            <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap'}}>
              {job.part_no && <span style={{fontWeight:600,fontFamily:'var(--mono)',color:'var(--c-navy)',fontSize:14}}>{job.part_no}</span>}
              {job.customer && <span style={{color:'var(--t-muted)',fontSize:13}}>· {job.customer}</span>}
              {job.description && <span style={{color:'var(--t-faint)',fontSize:12}}>· {job.description}</span>}
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            {['REVIEW','DRAFT','PLANNING'].includes(job.status) && (
              <button className="btn btn-navy" onClick={approve}><CheckCircle2 size={13}/> Approve Plan</button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{marginBottom:24}}>
        {[
          {k:'sequence',  l:'Forging Sequence'},
          {k:'overview',  l:'Part Details'},
          {k:'machines',  l:'Machine Analysis'},
          {k:'process',   l:'Process Flow'},
          {k:'toolcodes', l:'Tool Codes'},
        ].map(({k,l})=>(
          <button key={k} className={`tab ${tab===k?'active':''}`} onClick={()=>setTab(k as any)}>{l}</button>
        ))}
      </div>

      {/* FORGING SEQUENCE — primary visual view */}
      {tab === 'sequence' && (
        <div>
          {/* Summary strip */}
          <div style={{display:'flex',gap:0,marginBottom:20,background:'var(--c-navy)',borderRadius:10,overflow:'hidden'}}>
            {[
              {l:'Machine', v: activeMachineData?.name ?? '—'},
              {l:'Template', v:`Template ${template} — ${tpl?.name ?? ''}`},
              {l:'Wire Ø', v:`Ø${wire.toFixed(2)} mm`},
              {l:'Blank', v:`${blank.toFixed(1)} mm`},
              {l:'L/D', v:(blank/wire).toFixed(2)},
              {l:'Material', v:`${part.material_id} · Gr ${mat?.grade}`},
            ].map(({l,v},i) => (
              <div key={l} style={{
                flex:1, padding:'14px 18px',
                borderRight: i < 5 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                minWidth:0,
              }}>
                <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',textTransform:'uppercase',letterSpacing:'0.08em',marginBottom:4,fontWeight:600}}>{l}</div>
                <div style={{fontSize:13,fontWeight:600,color:'#fff',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{v}</div>
              </div>
            ))}
          </div>

          <div className="card card-pad">
            <SequenceViewer
              geoList={geoList}
              partName={job.part_no ?? undefined}
              wire={wire}
              blank={blank}
              template={template}
              machine={activeMachineData?.name}
            />
          </div>

          {/* Machine picker below visualiser */}
          {eligible.length > 0 && (
            <div className="card" style={{marginTop:16,padding:'14px 20px'}}>
              <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                <span style={{fontSize:12,fontWeight:600,color:'var(--t-muted)',textTransform:'uppercase',letterSpacing:'0.07em'}}>Machine:</span>
                {eligible.slice(0,4).map((m:any) => (
                  <button key={m.machine_id}
                    onClick={()=>{setSelectedMachine(m.machine_id);updateJob(job.id,{selected_machine:m.machine_id});}}
                    style={{
                      padding:'5px 14px', borderRadius:6, fontSize:12, fontWeight:500, cursor:'pointer',
                      border: activeMachine===m.machine_id ? '2px solid var(--c-navy)' : '1px solid var(--c-border)',
                      background: activeMachine===m.machine_id ? 'var(--c-navy)' : 'var(--c-surface)',
                      color: activeMachine===m.machine_id ? '#fff' : 'var(--t-secondary)',
                      transition:'all 0.12s',
                    }}>
                    {m.name}
                    <span style={{marginLeft:6,opacity:0.7,fontSize:11}}>{m.util}%</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div className="grid-2">
          <div className="card card-pad">
            <h3 style={{marginBottom:16}}>Part Specification</h3>
            {[
              ['Type', part.part_type],
              ['Nominal Ø', `Ø${part.nominal_diameter_mm}mm`],
              ['Thread', `M${part.nominal_diameter_mm}×${part.thread_pitch_mm}P`],
              ['Total Length', `${part.total_length_mm}mm`],
              part.shank_length_mm && ['Shank', `${part.shank_length_mm}mm`],
              part.nut_af_mm && ['A/F', `${part.nut_af_mm}mm`],
              part.nut_height_mm && ['Nut Height', `${part.nut_height_mm}mm`],
              part.wire_diameter_mm && ['Wire Ø', `Ø${part.wire_diameter_mm}mm`],
              part.blank_length_mm && ['Blank Length', `${part.blank_length_mm}mm`],
              part.weight_grams && ['Weight', `${part.weight_grams}g`],
              ['Material', `${part.material_id} — ${mat?.application}`],
              mat?.ht_q && ['HT Quench', `${mat.ht_q}°C`],
              mat?.ht_t && ['HT Temper', `${mat.ht_t}°C`],
              mat?.hrc && ['Target HRC', mat.hrc],
            ].filter(Boolean).map(([k,v]:any)=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',padding:'7px 0',borderBottom:'1px solid var(--c-border)',fontSize:13}}>
                <span style={{color:'var(--t-muted)'}}>{k}</span>
                <span className="val-pill">{v}</span>
              </div>
            ))}
          </div>
          <div>
            <div className="card card-pad" style={{marginBottom:16}}>
              <div style={{display:'flex',alignItems:'center',gap:16,marginBottom:16}}>
                <div style={{width:56,height:56,background:'var(--c-navy)',borderRadius:10,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span style={{fontSize:22,fontWeight:800,color:'#fff',letterSpacing:'-1px'}}>{template}</span>
                </div>
                <div>
                  <div style={{fontWeight:700,fontSize:15,color:'var(--c-navy)'}}>{tpl?.name}</div>
                  <div style={{fontSize:12,color:'var(--t-muted)',marginTop:2}}>{tpl?.trigger}</div>
                  <div style={{marginTop:6,display:'flex',gap:6}}>
                    <span className="badge badge-navy">{tpl?.family}</span>
                    <span className="badge badge-gray">{tpl?.stations} stations</span>
                    <span className={`badge ${confidence>0.85?'badge-green':'badge-amber'}`}>{(confidence*100).toFixed(0)}% conf.</span>
                  </div>
                </div>
              </div>
              {reasoning.map((r:string,i:number)=>(
                <div key={i} style={{fontSize:12,color:'var(--t-muted)',padding:'3px 0',borderBottom:i<reasoning.length-1?'1px solid var(--c-border)':'none'}}>
                  <span style={{color:'var(--c-blue)',marginRight:6}}>›</span>{r}
                </div>
              ))}
            </div>
            {job.engineer_notes && (
              <div className="card card-pad">
                <h3 style={{marginBottom:10}}>Engineer Notes</h3>
                <p style={{fontSize:13,lineHeight:1.7}}>{job.engineer_notes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* MACHINE ANALYSIS */}
      {tab === 'machines' && (
        <div className="card" style={{padding:0}}>
          <div className="card-header"><h3>Machine Compatibility — Template {template}</h3></div>
          <table>
            <thead><tr>
              <th>Machine</th><th>Type</th><th>Wire Range</th><th>Force</th>
              <th>Force Utilisation</th><th>Status</th><th>Blockers</th>
            </tr></thead>
            <tbody>
              {machineMatches.map((m:any)=>{
                const mach = MACHINES[m.machine_id];
                return (
                  <tr key={m.machine_id} style={{opacity:m.eligible?1:0.5}}>
                    <td style={{fontWeight:m.eligible?600:400,color:'var(--t-primary)'}}>{m.name}</td>
                    <td><span className="badge badge-navy">{mach.type}</span></td>
                    <td style={{fontFamily:'var(--mono)',fontSize:12}}>Ø{mach.wire_min}–{mach.wire_max}mm</td>
                    <td style={{fontFamily:'var(--mono)',fontSize:12,fontWeight:600}}>{(mach.force_kg/1000).toFixed(0)}t</td>
                    <td>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div className="progress-bar" style={{width:64}}>
                          <div className="progress-fill" style={{width:`${Math.min(m.util,100)}%`,background:m.util>85?'var(--c-red)':'var(--c-green)'}} />
                        </div>
                        <span style={{fontSize:12}}>{m.util}%</span>
                      </div>
                    </td>
                    <td>{m.eligible ? <span className="badge badge-green"><CheckCircle2 size={9}/> Eligible</span> : <span className="badge badge-red">Blocked</span>}</td>
                    <td style={{fontSize:12,color:'var(--t-muted)',maxWidth:200}}>{m.blockers.join(' · ')||'—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* PROCESS FLOW */}
      {tab === 'process' && (
        <div style={{maxWidth:540}}>
          <div className="card card-pad">
            <div className="flex-between" style={{marginBottom:20}}>
              <h3>Process Flow Chart</h3>
              <span style={{fontSize:11,color:'var(--t-faint)',fontFamily:'var(--mono)'}}>SFX-PF · {new Date().toLocaleDateString('en-IN')}</span>
            </div>
            <div style={{background:'var(--c-surface2)',border:'1px solid var(--c-border)',borderRadius:8,padding:'10px 14px',marginBottom:20,fontSize:12}}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:6}}>
                {[['Part',job.part_no||'—'],['Material',`${part.material_id} · Gr ${mat?.grade}`],['Machine',activeMachineData?.name||'—'],['Template',`Template ${template}`]].map(([k,v])=>(
                  <div key={k} style={{display:'flex',justifyContent:'space-between'}}>
                    <span style={{color:'var(--t-muted)'}}>{k}</span>
                    <span style={{fontWeight:500,fontFamily:'var(--mono)',fontSize:11}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>
            {flow.map((step:string,i:number)=>(
              <div key={i} style={{display:'flex',gap:14,alignItems:'flex-start',marginBottom:0}}>
                <div style={{display:'flex',flexDirection:'column',alignItems:'center',flexShrink:0}}>
                  <div style={{
                    width:26,height:26,borderRadius:'50%',flexShrink:0,
                    background: i===0?'var(--c-blue-lt)': i===1?'var(--c-amber-lt)':'var(--c-surface3)',
                    border:`1px solid ${i===0?'var(--c-blue-mid)':i===1?'var(--c-amber-mid)':'var(--c-border)'}`,
                    display:'flex',alignItems:'center',justifyContent:'center',
                    fontSize:11,fontWeight:700,
                    color: i===0?'var(--c-blue)':i===1?'var(--c-amber)':'var(--t-muted)',
                  }}>{i+1}</div>
                  {i < flow.length-1 && <div style={{width:1,height:16,background:'var(--c-border)'}}/>}
                </div>
                <div style={{paddingBottom:i<flow.length-1?4:0,paddingTop:4}}>
                  <div style={{fontSize:13,fontWeight:i<2?600:400,color:i<2?'var(--t-primary)':'var(--t-secondary)'}}>{step.split('—')[0].trim()}</div>
                  {step.includes('—') && <div style={{fontSize:11,color:'var(--t-muted)',marginTop:1,fontFamily:'var(--mono)'}}>{step.split('—').slice(1).join('—').trim()}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TOOL CODES */}
      {tab === 'toolcodes' && (
        <div style={{maxWidth:600}}>
          <div className="card card-pad">
            <div className="flex-between" style={{marginBottom:16}}>
              <h3>Tool Code Register</h3>
              <button className="btn btn-secondary btn-sm" onClick={()=>{
                navigator.clipboard.writeText(geoList.filter(g=>g.stn>0).map(g=>`${prefix}${String(g.stn).padStart(3,'0')} — ${g.label}`).join('\n'));
                setCopied(true); setTimeout(()=>setCopied(false),1500);
              }}><Copy size={11}/>{copied?'Copied!':'Copy all'}</button>
            </div>
            <div style={{fontSize:12,color:'var(--t-muted)',marginBottom:16}}>
              Prefix: <span className="val-pill">{prefix}</span>
            </div>
            {geoList.filter(g=>g.stn>0).map(g=>{
              const code = `${prefix}${String(g.stn).padStart(3,'0')}`;
              return (
                <div key={g.stn} style={{display:'flex',gap:14,alignItems:'flex-start',padding:'10px 0',borderBottom:'1px solid var(--c-border)'}}>
                  <div style={{width:22,height:22,borderRadius:'50%',background:'var(--c-blue-lt)',border:'1px solid var(--c-blue-mid)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:'var(--c-blue)',flexShrink:0}}>{g.stn}</div>
                  <div style={{flex:1}}>
                    <div style={{fontFamily:'var(--mono)',fontSize:12,fontWeight:600,color:'var(--c-navy)',marginBottom:2}}>{code}</div>
                    <div style={{fontSize:12,color:'var(--t-secondary)'}}>{g.label}</div>
                    <div style={{fontSize:11,color:'var(--t-muted)',marginTop:2}}>
                      {[g.punchMat&&g.punchMat!=='—'&&`Insert: ${g.punchMat}`, g.palletStr&&g.palletStr!=='—'&&`Pallet: ${g.palletStr}`, `KO: ${g.koType??'—'}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              );
            })}
            {part.part_type==='NUT' && (
              <div style={{display:'flex',gap:14,alignItems:'flex-start',padding:'10px 0'}}>
                <div style={{width:22,height:22,borderRadius:'50%',background:'var(--c-surface3)',border:'1px solid var(--c-border)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:700,color:'var(--t-muted)',flexShrink:0}}>H</div>
                <div>
                  <div style={{fontFamily:'var(--mono)',fontSize:12,fontWeight:600,color:'var(--c-blue)',marginBottom:2}}>{prefix}9999 — HOLE PUNCH (Universal)</div>
                  <div style={{fontSize:12,color:'var(--t-secondary)'}}>Stn 6 — Pierce through-bore</div>
                  <div style={{fontSize:11,color:'var(--t-muted)',marginTop:2}}>M2 · Bore Ø{((d*0.84)-0.15).toFixed(2)}mm · Slug free-fall · Shared component</div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
